import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Wrench } from "lucide-react";
import Layout from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { API } from "../../App";
import {
  SERVICE_STATUS_LABELS, SERVICE_STATUS_COLORS, PRIORITY_COLORS, fmtDateTime,
} from "./_rentalHelpers";

const emptySR = {
  rental_unit_id: "", category: "plumbing", priority: "normal",
  description: "", status: "new",
  owner_approval_required: false, estimated_cost: "", notes: "", due_at: "",
};

export default function RentalServiceRequestsPage() {
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySR);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (priorityFilter !== "all") params.priority = priorityFilter;
      const [s, u, m] = await Promise.all([
        axios.get(`${API}/rentals/service-requests`, { params }),
        units.length ? Promise.resolve({ data: units }) : axios.get(`${API}/rentals/units`, { params: { limit: 500 } }),
        meta ? Promise.resolve({ data: meta }) : axios.get(`${API}/rentals/meta`),
      ]);
      setItems(s.data || []); setUnits(u.data || []); setMeta(m.data);
    } catch (e) {
      toast.error("Failed to load service requests");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter, priorityFilter]);

  const startCreate = () => { setEditing(null); setForm(emptySR); setShowCreate(true); };
  const startEdit = (s) => {
    setEditing(s);
    setForm({
      ...emptySR, ...s,
      estimated_cost: s.estimated_cost ?? "",
      due_at: s.due_at ? s.due_at.slice(0, 16) : "",
    });
    setShowCreate(true);
  };

  const submit = async () => {
    if (!editing && !form.rental_unit_id) { toast.error("Pick a unit"); return; }
    if (!form.description?.trim()) { toast.error("Description required"); return; }
    const payload = {
      ...form,
      estimated_cost: form.estimated_cost === "" ? null : Number(form.estimated_cost),
      due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
    };
    try {
      if (editing) {
        const upd = { ...payload }; delete upd.rental_unit_id;
        await axios.put(`${API}/rentals/service-requests/${editing.service_request_id}`, upd);
        toast.success("Updated");
      } else {
        await axios.post(`${API}/rentals/service-requests`, payload);
        toast.success("Service request raised");
      }
      setShowCreate(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Layout>
      <div className="px-4 sm:px-6 py-6 max-w-7xl mx-auto" data-testid="rental-service-requests-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Wrench className="w-6 h-6 text-emerald-600" /> Service Requests
            </h1>
            <p className="text-sm text-slate-500">Tenant & owner-raised maintenance / ops</p>
          </div>
          <Button onClick={startCreate} data-testid="create-service-btn" className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-1" /> New Service Request
          </Button>
        </div>

        <Card className="p-3 mb-3 flex flex-wrap items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]" data-testid="sr-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(SERVICE_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-[180px]" data-testid="sr-priority-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {(meta?.service_priorities || ["low", "normal", "high", "urgent"]).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </Card>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Raised</th>
                  <th className="px-3 py-2 text-left">Unit</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-left">Priority</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-400">Loading…</td></tr>}
                {!loading && items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No service requests.</td></tr>}
                {items.map(s => (
                  <tr key={s.service_request_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`sr-row-${s.service_request_id}`}>
                    <td className="px-3 py-2 text-xs">{fmtDateTime(s.created_at)}</td>
                    <td className="px-3 py-2 text-xs">
                      <p className="font-medium">{s.unit?.unit_number || "—"}</p>
                      <p className="text-slate-500">{s.unit?.project_name || ""}</p>
                    </td>
                    <td className="px-3 py-2 capitalize">{s.category}</td>
                    <td className="px-3 py-2">
                      <Badge className={PRIORITY_COLORS[s.priority] || "bg-slate-100"}>{s.priority}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={SERVICE_STATUS_COLORS[s.status] || "bg-slate-100"}>{SERVICE_STATUS_LABELS[s.status] || s.status}</Badge>
                    </td>
                    <td className="px-3 py-2 max-w-xs truncate text-xs text-slate-600" title={s.description}>{s.description}</td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(s)} data-testid={`edit-sr-${s.service_request_id}`}>Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Edit Service Request" : "Raise Service Request"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {!editing && (
                <div className="col-span-2">
                  <Label>Unit *</Label>
                  <Select value={form.rental_unit_id} onValueChange={v => setForm({ ...form, rental_unit_id: v })}>
                    <SelectTrigger data-testid="sr-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                    <SelectContent>
                      {units.map(u => <SelectItem key={u.rental_unit_id} value={u.rental_unit_id}>{u.unit_number} · {u.project_name || ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="sr-category-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta?.service_categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="sr-priority-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta?.service_priorities || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="sr-status-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SERVICE_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Estimated cost</Label><Input type="number" value={form.estimated_cost} onChange={e => setForm({ ...form, estimated_cost: e.target.value })} /></div>
              <div><Label>Due at</Label><Input type="datetime-local" value={form.due_at} onChange={e => setForm({ ...form, due_at: e.target.value })} /></div>
              <div className="flex items-center gap-2 pt-7"><Switch checked={form.owner_approval_required} onCheckedChange={v => setForm({ ...form, owner_approval_required: v })} /> <span className="text-sm">Owner approval required</span></div>
              <div className="col-span-2"><Label>Description *</Label><Textarea rows={3} data-testid="sr-description-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="submit-sr" className="bg-emerald-600 hover:bg-emerald-700">{editing ? "Save" : "Raise"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
